import Sidebar from "./sidebar";
import { getSidebarItems } from "./sidebar-items";
import { Avatar, Button, Card, CardBody, CardFooter, ScrollShadow, Spacer, Popover, PopoverTrigger, PopoverContent, } from "@heroui/react";
import { useUserStore, refreshUserStore } from "./login/loginStore";
import { useNavigate } from "react-router-dom";
import Logo from "./logo";
import { Icon } from "@iconify/react";
import { fetchData } from "@/utils/requests";
import { User, X } from "lucide-react";
import { useMenuStore, isMobile } from "@/stores/MenuStore";
import { useLangTimeStore } from "@/stores/LangTimeStore";
import { useMemo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export default function Menu() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const userLogged = useUserStore((state) => state);

    const logoutUser = () => {
        fetchData('/api/logout', () => {
            refreshUserStore();
        }, false);
    };

    const menuStore = useMenuStore((state) => state);
    const langTimeStore = useLangTimeStore((state) => state);
    const [isAffiliate, setIsAffiliate] = useState(false);
    useEffect(() => {
        if (!userLogged.logged) {
            setIsAffiliate(false);
            return;
        }
        fetchData(
            '/api/rbt_pay_affiliate_info',
            (info: { id_a?: string } | null) => setIsAffiliate(!!info?.id_a),
            false,
            () => setIsAffiliate(false),
        );
    }, [userLogged.logged]);

    const items = useMemo(
        () => getSidebarItems({ isAffiliate }),
        [i18n.language, isAffiliate],
    );

    if (!menuStore.menuOpened && isMobile())
        return null;


    return <div className={"flex items-center h-screen justify-start fixed z-50 animate-fade " + (isMobile() ? ' w-full' : '')}>
        <div className={"h-dvh" + (isMobile() ? ' w-full' : '')}>
            {isMobile() && <X onClick={() => menuStore.setMenuOpened(false)} size={32}
                className="z-50 cursor-pointer text-6xl text-default-500 hover:text-default-100 transition-colors fixed right-5 top-5" />}
            <div className="p-6 relative flex h-full w-100vw sm:w-72 flex-1 flex-col border-r-small border-divider">
                <div className="flex items-center gap-2 px-2 cursor-pointer" onClick={() => navigate('/')} >
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-foreground">
                        <Logo />
                    </div>
                    <span className="text-small font-bold uppercase text-default-500 hover:text-default-100 transition-colors">Craque Stats</span>
                </div>
                <Spacer y={12} />
                <div className="cursor-pointer flex items-center gap-3 px-4 pb-3"
                    onClick={userLogged.logged ? () => navigate('/my-account') : () => navigate('/login')} id="loginSection">
                    <Avatar className="border-primary-700 border-2" isBordered={false} size="sm" icon={<User />} />
                    <div className="flex flex-col">
                        <p
                            className="text-small font-medium text-default-600"
                            style={{
                                maxWidth: '160px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                            id="menuEmail"
                        >
                            {userLogged.logged ? t('menu.menu_1') : t('menu.menu_2')}
                        </p>
                        <p className="text-tiny text-default-400" id="menuId">
                            {userLogged.logged ? `` : t('menu.menu_3')}
                            {userLogged.premium &&
                                <span>
                                    {t('menu.menu_4')}
                                    <Icon icon="mdi:check-circle" fontSize={13} className="text-green-500 mx-1 mb-1 inline-block" />
                                </span>
                            }
                        </p>
                    </div>
                </div>
                <Sidebar defaultSelectedKey="home" items={items} />
                {!userLogged.premium && <ScrollShadow className="-mr-6 h-full max-h-full py-6 pr-6">
                    <Card className="mx-2 overflow-visible" shadow="sm">
                        <CardBody className="items-center py-5 text-center">
                            <h3 className="text-medium font-medium text-default-700">
                                {t('menu.menu_5')}
                                <span aria-label="rocket-emoji" className="ml-2" role="img">
                                    🚀
                                </span>
                            </h3>
                            <p className="p-4 text-small text-default-500">
                                {t('menu.menu_6')}
                            </p>
                        </CardBody>
                        <CardFooter className="absolute -bottom-8 justify-center">
                            <Button className="px-10 shadow-md" color="primary" radius="full" variant="shadow" onClick={() => navigate('/plans')} id="upgradeBtn">
                                {t('menu.menu_7')}
                            </Button>
                        </CardFooter>
                    </Card>
                </ScrollShadow>}
                <div className="mt-auto flex flex-row">
                    <Button
                        className="justify-start text-default-500 data-[hover=true]:text-foreground w-full"
                        startContent={
                            <Icon
                                className="rotate-180 text-default-500"
                                icon="solar:minus-circle-line-duotone"
                                width={24}
                            />
                        }
                        variant="light"
                        onClick={() => {
                            if (userLogged.logged) logoutUser();
                            else navigate('/login');
                        }}
                    >
                        {userLogged.logged ? t('menu.menu_8') : t('menu.menu_9')}
                    </Button>
                    <Popover showArrow offset={10} placement="bottom">
                        <PopoverTrigger>
                            <Button
                                className="text-default-500 data-[hover=true]:text-foreground w-1/6"
                                startContent={
                                    <Icon
                                        className="text-default-500"
                                        icon="solar:earth-linear"
                                        width={24}
                                    />
                                }
                                variant="light"
                            >
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[150px] p-1">
                            <div className="flex flex-col gap-1 w-full">
                                <Button
                                    className="justify-between text-default-500 data-[hover=true]:text-foreground w-full"
                                    variant="light"
                                    onClick={() => {
                                        langTimeStore.setLang('pt');
                                    }}>
                                    🇧🇷 Português
                                    {langTimeStore.lang === 'pt' && <Icon icon="mdi:check" className="text-green-500 inline-block" />}
                                </Button>
                                <Button
                                    className="justify-between text-default-500 data-[hover=true]:text-foreground w-full"
                                    variant="light"
                                    onClick={() => {
                                        langTimeStore.setLang('en');
                                    }}>
                                    🇺🇸 English
                                    {langTimeStore.lang === 'en' && <Icon icon="mdi:check" className="text-green-500 inline-block" />}
                                </Button>
                            </div>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>
        </div>
    </div >
}